(ns common.os
  (:require ["@mui/material/CircularProgress$default" :as CircularProgress]
            ["@mui/material/Stack$default" :as Stack]
            ["penpal" :as penpal]
            [cljs-bean.core :refer [->clj]]
            [common.db.coeffects.sessionstorage]
            [common.db.effects.sessionstorage]
            [common.resource.sso :as sso]
            [common.url-template :as url-tpl]
            [portal.os-access-modal.view]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(def ^:dynamic *running-in-test?* false)

(defn os-environment?
  "This is used to determine whether we should try to behave as in OS (do the penpal dance, change login, etc.).
  Two ways to enable: config (env.js) or query param - the latter is required in non-prod envs where there is no
  separate deployment with OS-specific env.js"
  []
  (and (not *running-in-test?*)
       ;; If running in iframe, the app is in the WPP Open
       (not (identical? js/window.top js/window.self))))

(rf/reg-event-fx ::dispatch-later
  (fn [_ [_ event]]
    {:fx [[:dispatch-later {:ms 1000 :dispatch event}]]}))

;; This is for penpal interop - we turn the callback into a re-frame event
(defn apply-os-context [value]
  (rf/dispatch-sync [::set-os-context (->clj value)]))

;; boot os session have to be called after on-navigate in order to work correctly
;; I'm adding delay here to make sure that navigate already fired
(defn apply-native-os-context [value]
  (rf/dispatch-sync [::dispatch-later [::set-os-context (->clj value)]]))

(defn set-token [value]
  (rf/dispatch [::set-token value]))

(rf/reg-event-fx ::init
  (fn [{:keys [db]} _]
    (let [context (-> db ::os :context)]
      {:db (cond-> (dissoc db :context)
             (empty? context) (assoc-in [::os :context-pending] true))
       ;; clearing session to force lemonpi token reload
       :fx [[:localstorage/remove ["session"]]
            [:async-flow {:id ::os-init-flow
                          :rules [{:when :seen-both?
                                   :events [::set-token ::set-os-context]
                                   :dispatch [::resolve-os-context]}
                                  {:when :seen?
                                   :events ::resolve-os-context-success
                                   :dispatch [::sso/sso-token-success]
                                   :halt? true}]}]]})))

(rf/reg-event-fx ::set-os-enabled
  (fn [{:keys [db]} [_ v]]
    {:db (assoc-in db [::os :enabled] v)}))

(rf/reg-event-db ::set-token
  (fn [db [_ v]]
    (update db ::os assoc :token v)))

(rf/reg-event-fx ::set-os-context
  (fn [{:keys [db]} [_ v]]
    (if (= (-> db ::os :context) v)
      {} ; do not apply same context
      {:db (-> db
               (update ::os assoc
                       :context-pending false
                       :context v)
               (update :context dissoc :agency-id :advertiser-id))
       :fx [(when (-> db ::os :token)
              [:dispatch [::resolve-os-context]])]})))

(rf/reg-event-fx ::resolve-os-context
  [(rf/inject-cofx :sub ::context)
   (rf/inject-cofx :sub ::token)
   (rf/inject-cofx :sub :url-context)]
  (fn [{:keys [url-context]
        ::keys [context token]} _]
    {:http
     {:method :post
      :uri (url-tpl/render :api/resolve-os-context url-context)
      :params {:os-bootstrapping-context context
               :sso-token token}
      :on-success [::resolve-os-context-success]
      :on-failure [::resolve-os-context-failure]}}))

(rf/reg-event-fx ::resolve-os-context-success
  (fn [{:keys [db]} [_ res]]
    (let [resolved-context (:resolved-create-context res)]
      {:db (-> db
               (update :context merge resolved-context {:resolved true})
               (update ::os merge (select-keys resolved-context [:campaign-id])))})))

(rf/reg-event-fx ::resolve-os-context-failure
  (fn [_ _]
    {:fx [[:dispatch [:router/update-controller-status
                      {:failure? true
                       :failure-props {:code "os_workspace_mapping_missing"
                                       :appbar false
                                       :sidebar false
                                       :button true
                                       :button-label "Contact Support"
                                       :button-event [:modal/open {:id :os/access-request}]}}]]]}))

;;
;; re-frame
;;

(rf/reg-sub ::os
  (fn [db _]
    (::os db)))

(rf/reg-sub ::context
  :<- [::os]
  (fn [os _]
    (:context os)))

(rf/reg-sub ::token
  :<- [::os]
  (fn [os _]
    (:token os)))

(rf/reg-sub ::theme
  :<- [::context]
  (fn [c _]
    (:theme c)))

(rf/reg-sub ::enabled
  :<- [::os]
  (fn [os _]
    (:enabled os)))

(rf/reg-sub ::disabled
  :<- [::enabled]
  (fn [enabled _]
    (not enabled)))

(rf/reg-sub ::pending
  :<- [::os]
  (fn [os _]
    (:context-pending os)))

(rf/reg-sub ::project-id
  :<- [::context]
  (fn [c _]
    (-> c :project :id)))

(rf/reg-sub ::workspace-id
  :<- [::context]
  (fn [c _]
    (-> c :workspace :azId)))

(rf/reg-sub ::tenant-id
  :<- [::context]
  (fn [c _]
    (-> c :tenant :id)))

(def ^:dynamic *penpal* nil)

(defn init-penpal []
  (let [penpal (.connectToParent penpal #js {:parentOrigin #"(https://.*\.wpp-stage\.os-dev\.io|https://.*\.os\.wpp\.com)"
                                             :methods #js {:receiveOsContext apply-os-context}
                                             :debug true
                                             :timeout 500})]
    (set! *penpal* penpal)
    (-> (.-promise penpal)
        (.then (fn [^js r]
                 (rf/dispatch [::set-os-enabled true])
                 (.getAccessToken (.-osApi r))))
        (.then (fn [token]
                 (set-token token)))
        (.catch (fn [_e]
                  ;; No connection to OS parent frame,
                  ;; thrown if no reply without timeout.
                  )))
    (fn []
      (set! *penpal* nil)
      ((.-destroy penpal)))))

(defui os-provider [{:keys [children]}]
  (let [enabled (use-subscribe [::enabled])
        pending (use-subscribe [::pending])]
    (uix/use-effect (fn []
                      (when (os-environment?)
                        (rf/dispatch-sync [::init])
                        (init-penpal)))
                    [os-environment?])
    (if (and enabled pending)
      ;; NOTE: This is outside MUI ThemeProvider, so this is using default colors.
      ($ Stack
         {:height "100%"
          :alignItems "center"
          :justifyContent "center"}
         ($ CircularProgress
            {:variant "indeterminate"
             :size 70}))
      children)))
